/**
 * Topic icon + accent maps.
 *
 * Both are STATIC lookups on purpose:
 *  - lucide icons must be imported statically to be bundled;
 *  - Tailwind's JIT compiler only emits classes it can see as literal strings, so
 *    building a class name like `bg-${accent}-50` at runtime would silently produce
 *    an unstyled tile.
 *
 * That constraint is why an admin picks an icon and an accent from a LIST rather
 * than typing one: a typed name that isn't in these maps renders as a grey
 * BookOpen tile and nobody finds out until a user sees it.
 *
 * (If you are ever tempted to build a class at runtime, the supported escape
 * hatch is `safelist` in tailwind.config.js — but don't; the list below is the
 * whole design system for tiles and it should stay readable.)
 */

import {
  Coins,
  Gauge,
  ShieldAlert,
  Scale,
  LineChart,
  GraduationCap,
  BookOpen,
  PiggyBank,
  Landmark,
  Home,
  KeyRound,
  Percent,
  Calculator,
  CalendarClock,
  Wallet,
  Banknote,
  Umbrella,
  ShieldCheck,
  TrendingUp,
  Users,
  FileText,
  type LucideIcon,
} from 'lucide-react'

const ICONS: Record<string, LucideIcon> = {
  Coins,
  Gauge,
  ShieldAlert,
  Scale,
  LineChart,
  GraduationCap,
  BookOpen,
  PiggyBank,
  Landmark,
  Home,
  KeyRound,
  Percent,
  Calculator,
  CalendarClock,
  Wallet,
  Banknote,
  Umbrella,
  ShieldCheck,
  TrendingUp,
  Users,
  FileText,
}

/** For the admin content editor's icon picker. */
export const ICON_NAMES = Object.keys(ICONS)

export function topicIcon(name?: string): LucideIcon {
  return (name && ICONS[name]) || BookOpen
}

export interface TopicAccent {
  tile: string
  iconWrap: string
  ring: string
}

/**
 * Named palette. Text colours stay dark (gray-700+) for AA contrast — the accent
 * only ever tints backgrounds and borders, so contrast is preserved by
 * construction rather than by checking each combination.
 */
const ACCENT_BY_NAME: Record<string, TopicAccent> = {
  blue: {
    tile: 'border-blue-200 hover:border-blue-400 bg-blue-50/40',
    iconWrap: 'bg-blue-100 text-blue-800',
    ring: 'focus-visible:ring-blue-500',
  },
  amber: {
    tile: 'border-amber-200 hover:border-amber-400 bg-amber-50/40',
    iconWrap: 'bg-amber-100 text-amber-900',
    ring: 'focus-visible:ring-amber-500',
  },
  red: {
    tile: 'border-red-200 hover:border-red-400 bg-red-50/40',
    iconWrap: 'bg-red-100 text-red-800',
    ring: 'focus-visible:ring-red-500',
  },
  emerald: {
    tile: 'border-emerald-200 hover:border-emerald-400 bg-emerald-50/40',
    iconWrap: 'bg-emerald-100 text-emerald-800',
    ring: 'focus-visible:ring-emerald-500',
  },
  indigo: {
    tile: 'border-indigo-200 hover:border-indigo-400 bg-indigo-50/40',
    iconWrap: 'bg-indigo-100 text-indigo-800',
    ring: 'focus-visible:ring-indigo-500',
  },
  purple: {
    tile: 'border-purple-200 hover:border-purple-400 bg-purple-50/40',
    iconWrap: 'bg-purple-100 text-purple-800',
    ring: 'focus-visible:ring-purple-500',
  },
  teal: {
    tile: 'border-teal-200 hover:border-teal-400 bg-teal-50/40',
    iconWrap: 'bg-teal-100 text-teal-800',
    ring: 'focus-visible:ring-teal-500',
  },
  sky: {
    tile: 'border-sky-200 hover:border-sky-400 bg-sky-50/40',
    iconWrap: 'bg-sky-100 text-sky-800',
    ring: 'focus-visible:ring-sky-500',
  },
  violet: {
    tile: 'border-violet-200 hover:border-violet-400 bg-violet-50/40',
    iconWrap: 'bg-violet-100 text-violet-800',
    ring: 'focus-visible:ring-violet-500',
  },
  orange: {
    tile: 'border-orange-200 hover:border-orange-400 bg-orange-50/40',
    iconWrap: 'bg-orange-100 text-orange-900',
    ring: 'focus-visible:ring-orange-500',
  },
  rose: {
    tile: 'border-rose-200 hover:border-rose-400 bg-rose-50/40',
    iconWrap: 'bg-rose-100 text-rose-800',
    ring: 'focus-visible:ring-rose-500',
  },
}

/** For the admin content editor's accent picker. */
export const ACCENT_NAMES = Object.keys(ACCENT_BY_NAME)

/**
 * Legacy per-slug map for the six original student tiles.
 *
 * Kept verbatim so the student portal stays pixel-identical after the move to
 * named accents — those tiles have no `accent` field in the database and would
 * otherwise all fall back to grey.
 */
const LEGACY_SLUG_ACCENTS: Record<string, string> = {
  'financial-instruments': 'blue',
  'risk-level': 'amber',
  'red-flags': 'red',
  regulation: 'emerald',
  'stock-indices': 'indigo',
  'final-exam': 'purple',
}

const DEFAULT_ACCENT: TopicAccent = {
  tile: 'border-gray-200 hover:border-gray-400 bg-white',
  iconWrap: 'bg-gray-100 text-gray-700',
  ring: 'focus-visible:ring-primary-500',
}

/**
 * Resolution order: the topic's own accent → the legacy per-slug map → grey.
 * A grey tile is a deliberate "someone forgot to pick a colour" signal, not a
 * crash, so an admin-created topic still looks intentional.
 */
export function topicAccent(slug: string, accent?: string | null): TopicAccent {
  if (accent && ACCENT_BY_NAME[accent]) return ACCENT_BY_NAME[accent]
  const legacy = LEGACY_SLUG_ACCENTS[slug]
  if (legacy) return ACCENT_BY_NAME[legacy]
  return DEFAULT_ACCENT
}
